import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Welcome.css';

function Welcome() {
  const { isAuthenticated } = useAuth();
  const [authUrl, setAuthUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Get Salesforce OAuth authorization URL from backend
    fetch('/api/auth/login')
      .then(res => {
        if (!res.ok) {
          return res.json().then(data => {
            throw new Error(data.detail || `HTTP error! status: ${res.status}`);
          });
        }
        return res.json();
      })
      .then(data => {
        if (data.authorization_url) {
          setAuthUrl(data.authorization_url);
        } else if (data.detail) {
          setError(data.detail);
        }
      })
      .catch(err => {
        const errorMessage = err.message || 'Failed to initialize authentication';
        setError(errorMessage);
        console.error('Authentication initialization error:', err);
      });
  }, []);

  const handleLogin = () => {
    if (authUrl) {
      setLoading(true);
      window.location.href = authUrl;
    }
  };

  const handleGoToApp = () => {
    navigate('/package-deploy');
  };

  if (isAuthenticated) {
    return (
      <div className="main-content">
        <div className="alert-success">
          You are successfully logged in!
        </div>
        <h3>What's Next?</h3>
        <p>
          You can now access the core features of the application using the navigation sidebar on the left.
        </p>
        <ul className="feature-list">
          <li><strong>🚀 Package & Deploy</strong>: Navigate here to begin packaging a dashboard or deploying an existing package.</li>
          <li><strong>📖 Documentation</strong>: Find detailed instructions on how to use the tool.</li>
          <li><strong>💬 Support</strong>: Get help or report an issue.</li>
        </ul>
        <button className="button-primary" onClick={handleGoToApp}>
          Go to the Package & Deploy app 🚀
        </button>
      </div>
    );
  }

  return (
    <div className="main-content">
      <h1 className="gradient-text welcome-title">
        Welcome to the Tableau Next Package & Deploy app!
      </h1>
      <p className="welcome-subtitle">
        A streamlined tool to package your dashboards and deploy them to new Salesforce orgs with ease.
      </p>
      <hr className="divider" />

      <h2>Key Features</h2>
      <div className="features-grid">
        <div className="feature-card">
          <h4>📦 Automated Packaging</h4>
          <p>
            Select a dashboard and the app automatically finds all dependencies and packages them into a single, deployable file.
          </p>
        </div>
        <div className="feature-card">
          <h4>🚀 Guided Deployment</h4>
          <p>
            Deploy packages to any Salesforce org. The app guides you through creating new assets or mapping to existing ones to prevent conflicts.
          </p>
        </div>
        <div className="feature-card">
          <h4>🔗 Dependency Management</h4>
          <p>
            A validation step checks your package against the target org, highlighting any missing fields and allowing you to map them before you deploy.
          </p>
        </div>
      </div>

      <div className="container-bordered login-container">
        <h3>Please Login to Your Tableau Next Org to Get Started</h3>
        <p>
          This application requires authentication with your Salesforce account to access your Tableau Next assets.
        </p>
        
        {error && (
          <div className="alert-error">
            {error}
          </div>
        )}

        <button 
          onClick={handleLogin}
          className="button-primary button-large" 
          disabled={loading || !authUrl}
        >
          {loading ? 'Redirecting to Salesforce...' : 'Login with Salesforce'}
        </button>

        <div className="login-help">
          <p className="help-text">
            <strong>Need help?</strong> You'll be redirected to Salesforce to log in securely. 
            After authentication, you'll be brought back to this application.
          </p>
        </div>
      </div>

      <hr className="divider" />
      <p className="disclaimer">
        This tool is intended for internal use and demo purposes only!
      </p>
    </div>
  );
}

export default Welcome;
